import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  KitDetail,
  KitImage,
  KitItemAddOutcome,
  KitSaleMetadata,
  KitStitchingJob,
  KitTemplate,
} from '../shared/types';
import { normalizeCardImages } from '../shared/types';
import {
  useCatalogApi,
  type CatalogListCardRecord,
  type CatalogFilters,
  type CatalogListResponse,
} from '../catalog/catalog-api';
import {
  getKitReviewReadiness,
  kitAddOutcomeLabel,
  kitCardAddHint,
  kitCardIdentityLabel,
  moveKitCardId,
  retryableKitJobs,
  splitKitImages,
  summarizeKitImages,
  summarizeKitJobs,
} from './kit-ui';
import { useKitTemplatesApi } from './kit-templates-api';
import {
  kitTemplateBackgroundColor,
  kitTemplateBackgroundLabel,
  kitTemplateBackgroundPreviewUrl,
  kitTemplateCardScaleLabel,
} from './kit-templates-ui';
import { useKitsApi } from './kits-api';

const KIT_CARD_SEARCH_DEFAULT_LIMIT = 5;
const KIT_CARD_SEARCH_LIMIT_OPTIONS = [5, 10, 25, 50] as const;
const KIT_CARD_SEARCH_RESTORE_PREFIX = 'tcvs.kitCardSearchRestore.';

function failMessage(result: { ok: false; error: string; message?: string }): string {
  return result.message ?? result.error;
}

function formatDate(value: string | undefined): string {
  if (value === undefined) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

interface KitCardSearchRestoreState {
  filters: CatalogFilters;
  page: CatalogListResponse | null;
  scrollY: number;
}

function readKitCardSearchRestore(key: string): KitCardSearchRestoreState | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<KitCardSearchRestoreState>;
    if (parsed.filters === undefined || parsed.page === undefined) return null;
    return {
      filters: parsed.filters,
      page: parsed.page,
      scrollY: typeof parsed.scrollY === 'number' ? parsed.scrollY : 0,
    };
  } catch {
    return null;
  }
}

function writeKitCardSearchRestore(key: string, state: KitCardSearchRestoreState): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Best-effort navigation restore only; storage can be unavailable in private modes.
  }
}

type KitMetadataForm = Required<Pick<KitSaleMetadata, 'title' | 'description'>>;

function metadataFromKit(kit: KitDetail | null): KitMetadataForm {
  return {
    title: kit?.title ?? '',
    description: kit?.description ?? '',
  };
}

function metadataBody(form: KitMetadataForm, templateId: string): KitSaleMetadata {
  return {
    title: form.title,
    description: form.description,
    ...(templateId.trim() !== '' ? { templateId: templateId.trim() } : {}),
  };
}

export function KitDetailPage(): JSX.Element {
  const { kitId } = useParams<{ kitId: string }>();
  const searchRestoreKey = `${KIT_CARD_SEARCH_RESTORE_PREFIX}${kitId ?? 'unknown'}`;
  const [restoredSearch] = useState(() => readKitCardSearchRestore(searchRestoreKey));
  const api = useKitsApi();
  const templatesApi = useKitTemplatesApi();
  const catalogApi = useCatalogApi();
  const navigate = useNavigate();
  const [kit, setKit] = useState<KitDetail | null>(null);
  const [metadata, setMetadata] = useState<KitMetadataForm>(metadataFromKit(null));
  const [templates, setTemplates] = useState<readonly KitTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [kitAction, setKitAction] = useState<'complete' | 'reopen' | 'retry' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [mutatingCardId, setMutatingCardId] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<CatalogFilters>(
    restoredSearch?.filters ?? {
      limit: KIT_CARD_SEARCH_DEFAULT_LIMIT,
      imageQualityStatus: 'ACCEPTED',
    },
  );
  const [searchPage, setSearchPage] = useState<CatalogListResponse | null>(
    restoredSearch?.page ?? null,
  );
  const latestSearchRestore = useRef<KitCardSearchRestoreState>({
    filters: restoredSearch?.filters ?? {
      limit: KIT_CARD_SEARCH_DEFAULT_LIMIT,
      imageQualityStatus: 'ACCEPTED',
    },
    page: restoredSearch?.page ?? null,
    scrollY: restoredSearch?.scrollY ?? 0,
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [outcomes, setOutcomes] = useState<readonly KitItemAddOutcome[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (kitId === undefined || kitId.trim() === '') {
      setError('missing kit id');
      return;
    }
    setLoading(true);
    setError(null);
    const res = await api.get(kitId);
    setLoading(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setKit(res.data);
    setMetadata(metadataFromKit(res.data));
    setSelectedTemplateId(res.data.templateId ?? '');
  }, [api, kitId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    const res = await templatesApi.list();
    setTemplatesLoading(false);
    if (!res.ok) {
      setTemplatesError(failMessage(res));
      return;
    }
    setTemplates(
      res.data.templates
        .filter((template) => template.status === 'ACTIVE')
        .sort(compareTemplatesForKit),
    );
  }, [templatesApi]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const editable = kit?.status === 'DRAFT';

  const saveMetadata = useCallback(
    async (nextMetadata: KitMetadataForm, nextTemplateId: string) => {
      if (kit === null) return;
      setSaving(true);
      setError(null);
      setMessage(null);
      const res = await api.update(kit.kitId, metadataBody(nextMetadata, nextTemplateId));
      setSaving(false);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKit(res.data);
      setMetadata(metadataFromKit(res.data));
      setSelectedTemplateId(res.data.templateId ?? '');
      setMessage('Kit details saved.');
    },
    [api, kit],
  );

  const onMetadataBlur = useCallback(() => {
    if (!editable || saving) return;
    void saveMetadata(metadata, selectedTemplateId);
  }, [editable, metadata, saveMetadata, saving, selectedTemplateId]);

  const onSelectTemplate = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
      if (!editable || saving) return;
      void saveMetadata(metadata, templateId);
    },
    [editable, metadata, saveMetadata, saving],
  );

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.templateId === selectedTemplateId),
    [selectedTemplateId, templates],
  );
  const reviewReadiness = useMemo(() => (kit === null ? null : getKitReviewReadiness(kit)), [kit]);
  const selectedCardIds = useMemo(
    () => new Set(kit?.items.map((item) => item.cardId) ?? []),
    [kit],
  );

  const searchCards = useCallback(
    async (filtersOverride?: CatalogFilters) => {
      const filters = filtersOverride ?? searchFilters;
      setSearchLoading(true);
      setSearchError(null);
      const res = await catalogApi.list(filters);
      setSearchLoading(false);
      if (!res.ok) {
        setSearchError(failMessage(res));
        return;
      }
      setSearchPage(res.data);
    },
    [catalogApi, searchFilters],
  );

  useEffect(() => {
    latestSearchRestore.current = {
      filters: searchFilters,
      page: searchPage,
      scrollY: window.scrollY,
    };
  }, [searchFilters, searchPage]);

  const persistSearchRestore = useCallback(
    (scrollY = window.scrollY) => {
      writeKitCardSearchRestore(searchRestoreKey, { ...latestSearchRestore.current, scrollY });
    },
    [searchRestoreKey],
  );

  useEffect(() => {
    if (restoredSearch?.scrollY === undefined || searchPage === null) return;
    const id = window.setTimeout(() => window.scrollTo({ top: restoredSearch.scrollY }), 0);
    return () => window.clearTimeout(id);
  }, [restoredSearch, searchPage]);

  useEffect(() => {
    return () => {
      persistSearchRestore();
    };
  }, [persistSearchRestore]);

  const addCard = useCallback(
    async (cardId: string) => {
      if (kit === null || !editable) return;
      setMutatingCardId(cardId);
      setError(null);
      setMessage(null);
      const res = await api.addItems(kit.kitId, { cardIds: [cardId] });
      setMutatingCardId(null);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKit(res.data.kit);
      setOutcomes(res.data.outcomes);
      const added = res.data.outcomes.filter((outcome) => outcome.status === 'added').length;
      setMessage(added === 1 ? 'Card added to Kit.' : 'Card was not added.');
    },
    [api, editable, kit],
  );

  const removeCard = useCallback(
    async (cardId: string) => {
      if (kit === null || !editable) return;
      setMutatingCardId(cardId);
      setError(null);
      setMessage(null);
      const res = await api.removeItem(kit.kitId, cardId);
      setMutatingCardId(null);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKit(res.data);
      setOutcomes([]);
      setMessage('Card removed from Kit.');
    },
    [api, editable, kit],
  );

  const moveCard = useCallback(
    async (cardId: string, direction: 'up' | 'down') => {
      if (kit === null || !editable) return;
      const currentCardIds = kit.items.map((item) => item.cardId);
      const nextCardIds = moveKitCardId(currentCardIds, cardId, direction);
      if (nextCardIds === currentCardIds) return;
      setMutatingCardId(cardId);
      setError(null);
      setMessage(null);
      const res = await api.reorderItems(kit.kitId, { cardIds: nextCardIds });
      setMutatingCardId(null);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKit(res.data);
      setMessage('Kit card order updated.');
    },
    [api, editable, kit],
  );

  const completeKit = useCallback(async () => {
    if (kit === null || kit.status !== 'DRAFT') return;
    setKitAction('complete');
    setError(null);
    setMessage(null);
    const res = await api.complete(kit.kitId);
    setKitAction(null);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setKit(res.data.kit);
    setOutcomes([]);
    setMessage(
      res.data.job !== undefined
        ? 'Kit completed. Stitching job queued.'
        : 'Kit is already complete.',
    );
  }, [api, kit]);

  const reopenKit = useCallback(async () => {
    if (kit === null || kit.status !== 'COMPLETE') return;
    setKitAction('reopen');
    setError(null);
    setMessage(null);
    const res = await api.reopen(kit.kitId);
    setKitAction(null);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setKit(res.data);
    setMessage('Kit reopened as a draft.');
  }, [api, kit]);

  const retryJob = useCallback(
    async (jobId: string) => {
      if (kit === null) return;
      setKitAction('retry');
      setError(null);
      setMessage(null);
      const res = await api.retryJob(kit.kitId, jobId);
      setKitAction(null);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setKit(res.data.kit);
      setMessage('Stitching job retry queued.');
    },
    [api, kit],
  );

  const deleteKit = useCallback(async () => {
    if (kit === null) return;
    const label = kit.title ?? kit.kitId;
    if (!window.confirm(`Delete Kit "${label}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    setMessage(null);
    const res = await api.deleteKit(kit.kitId);
    setDeleting(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    navigate('/kitting');
  }, [api, kit, navigate]);

  return (
    <div>
      <p>
        <Link to="/kitting">Back to Kits</Link>
      </p>
      <h2>{kit?.title ?? 'Kit'}</h2>
      {kit !== null ? <p className="tcvs-mono">{kit.kitId}</p> : null}

      <div className="tcvs-toolbar">
        <button type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        {kit !== null ? (
          <button
            type="button"
            className="tcvs-btn-secondary"
            onClick={() => void deleteKit()}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Kit'}
          </button>
        ) : null}
      </div>

      {error !== null ? (
        <p className="tcvs-error-text" role="alert">
          {error}
        </p>
      ) : null}
      {message !== null ? <p className="tcvs-success-text">{message}</p> : null}

      {kit !== null ? (
        <>
          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Kit Details</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              <label className="tcvs-field">
                <span>Title</span>
                <input
                  type="text"
                  value={metadata.title}
                  disabled={!editable || saving}
                  onChange={(e) => setMetadata((prev) => ({ ...prev, title: e.target.value }))}
                  onBlur={onMetadataBlur}
                  placeholder="e.g. Five-card vintage lot"
                />
              </label>
              <label className="tcvs-field">
                <span>Description</span>
                <input
                  type="text"
                  value={metadata.description}
                  disabled={!editable || saving}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, description: e.target.value }))
                  }
                  onBlur={onMetadataBlur}
                />
              </label>
              <KitTemplatePicker
                templates={templates}
                selectedTemplateId={selectedTemplateId}
                kitItemCount={kit.itemCount}
                disabled={!editable || saving}
                loading={templatesLoading}
                error={templatesError}
                onRefresh={() => void loadTemplates()}
                onChange={onSelectTemplate}
              />
              {!editable ? (
                <p className="tcvs-muted" style={{ margin: 0 }}>
                  Kit details are editable only while the Kit is a draft.
                </p>
              ) : null}
              {editable && saving ? (
                <p className="tcvs-muted" style={{ margin: 0 }}>
                  Saving Kit details...
                </p>
              ) : null}
            </div>
          </section>

          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Search Cards</h3>
            {editable ? (
              <>
                <KitCardSearchForm
                  value={searchFilters}
                  loading={searchLoading}
                  onSubmit={(next) => {
                    setSearchFilters(next);
                    void searchCards(next);
                  }}
                />
                {searchError !== null ? (
                  <p className="tcvs-error-text" role="alert">
                    {searchError}
                  </p>
                ) : null}
                {outcomes.length > 0 ? <KitAddOutcomes outcomes={outcomes} /> : null}
                {searchPage !== null ? (
                  <KitSearchResults
                    items={searchPage.items}
                    selectedCardIds={selectedCardIds}
                    mutatingCardId={mutatingCardId}
                    onBeforeOpen={persistSearchRestore}
                    onAdd={(cardId) => void addCard(cardId)}
                  />
                ) : (
                  <p className="tcvs-muted">
                    Search to display cards that can be added to this Kit.
                  </p>
                )}
                {searchPage !== null && searchPage.lastKey !== null ? (
                  <button
                    type="button"
                    className="tcvs-btn-secondary"
                    disabled={searchLoading}
                    onClick={() => {
                      const next = { ...searchFilters, lastKey: searchPage.lastKey ?? undefined };
                      setSearchFilters(next);
                      void searchCards(next);
                    }}
                  >
                    {searchLoading ? 'Loading...' : 'Next page'}
                  </button>
                ) : null}
              </>
            ) : (
              <p className="tcvs-muted">Card search is available only while the Kit is a draft.</p>
            )}
          </section>

          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Selected Cards</h3>
            <SelectedCardsPanel
              kit={kit}
              readiness={reviewReadiness}
              busyAction={kitAction}
              editable={editable}
              mutatingCardId={mutatingCardId}
              onRemove={(cardId) => void removeCard(cardId)}
              onMove={(cardId, direction) => void moveCard(cardId, direction)}
              onComplete={() => void completeKit()}
              onReopen={() => void reopenKit()}
            />
          </section>

          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Summary</h3>
            <dl className="tcvs-detail-list">
              <div>
                <dt>Status</dt>
                <dd>{kit.status}</dd>
              </div>
              <div>
                <dt>Cards</dt>
                <dd>{kit.itemCount}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatDate(kit.updatedAt)}</dd>
              </div>
              <div>
                <dt>Template</dt>
                <dd>{selectedTemplate?.name ?? kit.templateId ?? '-'}</dd>
              </div>
              <div>
                <dt>Images</dt>
                <dd>{summarizeKitImages(kit.images)}</dd>
              </div>
              <div>
                <dt>Jobs</dt>
                <dd>{summarizeKitJobs(kit.jobs)}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatDate(kit.createdAt)}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{formatDate(kit.completedAt)}</dd>
              </div>
            </dl>
          </section>

          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Generated Images</h3>
            <GeneratedImagesPanel images={kit.images} />
          </section>

          <section className="tcvs-card">
            <h3 style={{ marginTop: 0 }}>Jobs</h3>
            <JobsPanel
              jobs={kit.jobs}
              busyAction={kitAction}
              onRetry={(jobId) => void retryJob(jobId)}
            />
          </section>
        </>
      ) : null}
    </div>
  );
}

function KitTemplatePicker({
  templates,
  selectedTemplateId,
  kitItemCount,
  disabled,
  loading,
  error,
  onRefresh,
  onChange,
}: {
  templates: readonly KitTemplate[];
  selectedTemplateId: string;
  kitItemCount: number;
  disabled: boolean;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onChange: (templateId: string) => void;
}): JSX.Element {
  return (
    <fieldset className="tcvs-kit-template-picker" disabled={disabled}>
      <legend>Template</legend>
      <div className="tcvs-kit-template-picker-header">
        <span className="tcvs-caption">Choose the visual template for this Kit.</span>
        <button type="button" className="tcvs-link-button" disabled={loading} onClick={onRefresh}>
          {loading ? 'Loading...' : 'Refresh templates'}
        </button>
      </div>
      {selectedTemplateId === '' ? (
        <p className="tcvs-muted">
          No template is pinned yet. The active default for the completed card count will be used
          unless you choose one.
        </p>
      ) : null}
      {error !== null ? (
        <p className="tcvs-error-text" role="alert">
          {error}
        </p>
      ) : null}
      {templates.length === 0 ? (
        <p className="tcvs-muted">No active templates are available.</p>
      ) : (
        <div className="tcvs-kit-template-picker-options" role="radiogroup">
          {templates.map((template) => {
            const selected = template.templateId === selectedTemplateId;
            const countMismatch = kitItemCount > 0 && template.cardCount !== kitItemCount;
            return (
              <button
                key={template.templateId}
                type="button"
                className="tcvs-kit-template-picker-option"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => onChange(template.templateId)}
              >
                <span
                  className="tcvs-kit-template-picker-thumb"
                  style={{ backgroundColor: kitTemplateBackgroundColor(template) }}
                >
                  <KitTemplatePickerThumb template={template} />
                </span>
                <span className="tcvs-kit-template-picker-meta">
                  <strong>{templateOptionLabel(template)}</strong>
                  <span>
                    {template.cardCount} cards - {kitTemplateCardScaleLabel(template)} scale
                  </span>
                  <span>{kitTemplateBackgroundLabel(template.background)}</span>
                  {countMismatch ? (
                    <span className="tcvs-kit-template-picker-warning">
                      Current Kit has {kitItemCount} cards
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

function KitTemplatePickerThumb({ template }: { template: KitTemplate }): JSX.Element {
  const previewUrl = kitTemplateBackgroundPreviewUrl(template);
  if (previewUrl !== undefined) {
    return <img src={previewUrl} alt="" aria-hidden="true" />;
  }
  if (template.background.kind === 'designed') {
    return (
      <span
        className="tcvs-kit-template-background-designed tcvs-kit-template-background-designed-large"
        aria-hidden="true"
      />
    );
  }
  return <span className="tcvs-kit-template-background-color" aria-hidden="true" />;
}

function compareTemplatesForKit(a: KitTemplate, b: KitTemplate): number {
  if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
  if (a.cardCount !== b.cardCount) return a.cardCount - b.cardCount;
  if (a.source !== b.source) return a.source === 'operator' ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function templateOptionLabel(template: KitTemplate): string {
  const suffix = template.isDefault ? ' (default)' : '';
  return `${template.name}${suffix}`;
}

function GeneratedImagesPanel({ images }: { images: readonly KitImage[] }): JSX.Element {
  const grouped = splitKitImages(images);
  if (images.length === 0) return <p className="tcvs-muted">No generated images yet.</p>;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p className="tcvs-muted" style={{ margin: 0 }}>
        The system keeps the 3 most recent generated images for each Kit.
      </p>
      <KitImageGroup title="Current" images={grouped.current} />
      <KitImageGroup title="Stale / Superseded" images={grouped.stale} />
    </div>
  );
}

function KitImageGroup({
  title,
  images,
}: {
  title: string;
  images: readonly KitImage[];
}): JSX.Element | null {
  if (images.length === 0) return null;
  return (
    <div>
      <h4 style={{ margin: '0 0 8px' }}>{title}</h4>
      {images.map((image) => (
        <div key={image.imageId} style={{ marginBottom: 16 }}>
          <p style={{ marginTop: 0 }}>
            <strong>{image.status}</strong>{' '}
            <span className="tcvs-caption">
              {image.patternId} - {formatDate(image.createdAt)}
            </span>
            <br />
            <span className="tcvs-caption tcvs-mono">{image.s3Key}</span>
          </p>
          {image.url !== undefined ? (
            <img
              src={image.url}
              alt={`${image.status.toLowerCase()} Kit output`}
              style={{ maxWidth: 320, width: '100%', height: 'auto', display: 'block' }}
            />
          ) : (
            <p className="tcvs-muted">No preview URL returned.</p>
          )}
        </div>
      ))}
    </div>
  );
}

function JobsPanel({
  jobs,
  busyAction,
  onRetry,
}: {
  jobs: readonly KitStitchingJob[];
  busyAction: 'complete' | 'reopen' | 'retry' | null;
  onRetry: (jobId: string) => void;
}): JSX.Element {
  const retryable = new Set(retryableKitJobs(jobs).map((job) => job.jobId));
  if (jobs.length === 0) return <p className="tcvs-muted">No stitching jobs yet.</p>;
  return (
    <ul style={{ paddingLeft: 20 }}>
      {[...jobs]
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .map((job) => (
          <li key={job.jobId} style={{ marginBottom: 8 }}>
            <span className="tcvs-mono">{job.jobId}</span> - <strong>{job.status}</strong> - attempt{' '}
            {job.attempt}
            {job.errorMessage !== undefined ? (
              <span className="tcvs-error-text"> - {job.errorMessage}</span>
            ) : null}
            {retryable.has(job.jobId) ? (
              <button
                type="button"
                className="tcvs-btn-compact"
                disabled={busyAction !== null}
                onClick={() => onRetry(job.jobId)}
                style={{ marginLeft: 8 }}
              >
                {busyAction === 'retry' ? 'Retrying...' : 'Retry'}
              </button>
            ) : null}
          </li>
        ))}
    </ul>
  );
}

function KitCardSearchForm({
  value,
  loading,
  onSubmit,
}: {
  value: CatalogFilters;
  loading: boolean;
  onSubmit: (next: CatalogFilters) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<CatalogFilters>(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      className="tcvs-form-row"
      style={{ alignItems: 'end', marginBottom: 12 }}
      onSubmit={(e) => {
        e.preventDefault();
        const searchText = draft.q?.trim();
        const next: CatalogFilters = {
          limit: draft.limit ?? KIT_CARD_SEARCH_DEFAULT_LIMIT,
          ...(searchText !== undefined && searchText !== '' ? { q: searchText } : {}),
          imageQualityStatus: 'ACCEPTED',
        };
        onSubmit(next);
      }}
    >
      <label className="tcvs-field">
        <span>Search</span>
        <input
          type="text"
          value={draft.q ?? ''}
          placeholder="Player, team, set, SKU, short code..."
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, q: e.target.value || undefined, lastKey: undefined }))
          }
          style={{ width: 360 }}
        />
      </label>
      <label className="tcvs-field">
        <span>Results</span>
        <select
          value={draft.limit ?? KIT_CARD_SEARCH_DEFAULT_LIMIT}
          onChange={(e) =>
            setDraft((prev) => ({
              ...prev,
              limit: Number.parseInt(e.target.value, 10),
              lastKey: undefined,
            }))
          }
        >
          {KIT_CARD_SEARCH_LIMIT_OPTIONS.map((limit) => (
            <option key={limit} value={limit}>
              {limit}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" disabled={loading}>
        {loading ? 'Searching...' : 'Apply'}
      </button>
    </form>
  );
}

function KitAddOutcomes({ outcomes }: { outcomes: readonly KitItemAddOutcome[] }): JSX.Element {
  return (
    <ul className="tcvs-caption" style={{ marginTop: 0 }}>
      {outcomes.map((outcome) => (
        <li key={`${outcome.cardId}-${outcome.status}`}>
          <span className="tcvs-mono">{outcome.cardId}</span>: {kitAddOutcomeLabel(outcome)}
          {outcome.message !== undefined ? ` (${outcome.message})` : ''}
        </li>
      ))}
    </ul>
  );
}

function KitSearchResults({
  items,
  selectedCardIds,
  mutatingCardId,
  onBeforeOpen,
  onAdd,
}: {
  items: readonly CatalogListCardRecord[];
  selectedCardIds: ReadonlySet<string>;
  mutatingCardId: string | null;
  onBeforeOpen: () => void;
  onAdd: (cardId: string) => void;
}): JSX.Element {
  if (items.length === 0) return <p className="tcvs-muted">No cards match the current filters.</p>;
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12 }}>
      <thead>
        <tr>
          <th style={builderCellStyle}>Preview</th>
          <th style={builderCellStyle}>Card</th>
          <th style={builderCellStyle}>SKU</th>
          <th style={builderCellStyle}>Identity</th>
          <th style={builderCellStyle}>Availability</th>
          <th style={builderCellStyle}></th>
        </tr>
      </thead>
      <tbody>
        {items.map((card) => {
          const hint = kitCardAddHint(card, selectedCardIds);
          const busy = mutatingCardId === card.cardId;
          return (
            <tr key={card.cardId}>
              <td style={builderCellStyle}>
                <KitFrontImageBadge card={card} />
              </td>
              <td style={builderCellStyle}>
                <Link to={`/catalog/${encodeURIComponent(card.cardId)}`} onClick={onBeforeOpen}>
                  {card.shortCode ?? card.cardId}
                </Link>
                <div className="tcvs-caption tcvs-mono">{card.cardId}</div>
              </td>
              <td style={builderCellStyle}>{card.sku ?? '-'}</td>
              <td style={builderCellStyle}>{kitCardIdentityLabel(card)}</td>
              <td style={builderCellStyle}>
                <span
                  className={
                    hint.status === 'ready'
                      ? 'tcvs-tone-stored'
                      : hint.status === 'selected'
                        ? 'tcvs-muted'
                        : 'tcvs-tone-pulled'
                  }
                >
                  {hint.label}
                </span>
              </td>
              <td style={builderCellStyle}>
                <button
                  type="button"
                  className="tcvs-btn-compact"
                  disabled={busy || hint.status !== 'ready'}
                  onClick={() => onAdd(card.cardId)}
                >
                  {busy ? 'Adding...' : 'Add'}
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function KitFrontImageBadge({ card }: { card: CatalogListCardRecord }): JSX.Element {
  const hasFront = normalizeCardImages(card).front !== undefined;
  const imageUrl = card.imageUrls?.front ?? card.imageUrl;
  if (imageUrl !== undefined) {
    return (
      <img
        src={imageUrl}
        alt="Card front"
        style={{
          width: 56,
          height: 78,
          objectFit: 'cover',
          display: 'block',
          border: '1px solid var(--tcvs-border)',
          borderRadius: 3,
          background: 'var(--tcvs-th-bg)',
        }}
      />
    );
  }
  return (
    <span
      aria-label={hasFront ? 'Front image available' : 'No front image'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 56,
        height: 78,
        border: '1px solid var(--tcvs-border)',
        borderRadius: 3,
        background: hasFront ? 'var(--tcvs-th-bg)' : 'transparent',
        fontSize: 12,
        textAlign: 'center',
      }}
    >
      {hasFront ? 'Front' : 'No front'}
    </span>
  );
}

function SelectedCardsPanel({
  kit,
  readiness,
  busyAction,
  editable,
  mutatingCardId,
  onRemove,
  onMove,
  onComplete,
  onReopen,
}: {
  kit: KitDetail;
  readiness: ReturnType<typeof getKitReviewReadiness> | null;
  busyAction: 'complete' | 'reopen' | 'retry' | null;
  editable: boolean;
  mutatingCardId: string | null;
  onRemove: (cardId: string) => void;
  onMove: (cardId: string, direction: 'up' | 'down') => void;
  onComplete: () => void;
  onReopen: () => void;
}): JSX.Element {
  const completeDisabled =
    kit.status !== 'DRAFT' || readiness?.ready !== true || busyAction !== null;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {kit.items.length === 0 ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          No cards selected.
        </p>
      ) : (
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          {kit.items.map((item, index) => {
            const busy = mutatingCardId === item.cardId;
            return (
              <li key={item.cardId} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>
                    <Link to={`/catalog/${encodeURIComponent(item.cardId)}`}>
                      {item.card.identityDisplay ?? item.card.shortCode ?? item.cardId}
                    </Link>
                  </strong>
                  {item.card.sku !== undefined ? (
                    <span className="tcvs-caption">SKU {item.card.sku}</span>
                  ) : null}
                  {item.card.imageQualityStatus !== undefined ? (
                    <span className="tcvs-caption">Image QA {item.card.imageQualityStatus}</span>
                  ) : null}
                  {editable ? (
                    <>
                      <button
                        type="button"
                        className="tcvs-btn-compact"
                        disabled={busy || index === 0}
                        onClick={() => onMove(item.cardId, 'up')}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className="tcvs-btn-compact"
                        disabled={busy || index === kit.items.length - 1}
                        onClick={() => onMove(item.cardId, 'down')}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        className="tcvs-btn-compact tcvs-btn-danger"
                        disabled={busy}
                        onClick={() => onRemove(item.cardId)}
                      >
                        {busy ? 'Updating...' : 'Remove'}
                      </button>
                    </>
                  ) : null}
                </div>
                <div className="tcvs-caption tcvs-mono">{item.cardId}</div>
              </li>
            );
          })}
        </ol>
      )}
      {readiness !== null && readiness.messages.length > 0 ? (
        <ul className="tcvs-error-text" style={{ margin: 0 }}>
          {readiness.messages.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      ) : null}
      {kit.status === 'DRAFT' ? (
        <div>
          <button type="button" disabled={completeDisabled} onClick={onComplete}>
            {busyAction === 'complete' ? 'Completing...' : 'Complete Kit'}
          </button>
        </div>
      ) : null}
      {kit.status === 'COMPLETE' ? (
        <div>
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={busyAction !== null}
            onClick={onReopen}
          >
            {busyAction === 'reopen' ? 'Reopening...' : 'Reopen Kit'}
          </button>
        </div>
      ) : null}
      {kit.status === 'CANCELED' ? (
        <p className="tcvs-muted" style={{ margin: 0 }}>
          This Kit is canceled and cannot be completed.
        </p>
      ) : null}
    </div>
  );
}

const builderCellStyle = {
  border: '1px solid var(--tcvs-border)',
  padding: '4px 8px',
  textAlign: 'left' as const,
  verticalAlign: 'top' as const,
};
